import { useQuery } from '@tanstack/react-query';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Mic, Send, RefreshCw, AlertCircle, CheckCircle, Paperclip, Activity } from 'lucide-react';
import api from '../lib/api';
import { trackEvent } from '../lib/analytics';
import { uploadAttachment, ACCEPT_ATTR, MAX_FILES_PER_MESSAGE, MAX_UPLOAD_BYTES } from '../lib/uploads';
import { AttachmentChips, type PendingAttachment } from '../components/AttachmentChips';
import { useUiStore } from '../stores/uiStore';
import { useAuthStore } from '../stores/authStore';
import BottomNav from '../components/BottomNav';
import CardSkeleton from '../components/cards/CardSkeleton';
import VenturesCard from '../components/cards/VenturesCard';
import NewsCard from '../components/cards/NewsCard';

function renderInline(text: string): React.ReactNode[] {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1
      ? <strong key={i} style={{ fontWeight: 600, color: 'var(--gold)' }}>{part}</strong>
      : part
  );
}

function renderBriefContent(content: string) {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let paragraphLines: string[] = [];

  const flushParagraph = () => {
    const trimmed = paragraphLines.map(l => l.trim()).filter(Boolean);
    if (trimmed.length > 0) {
      const nodes: React.ReactNode[] = [];
      trimmed.forEach((line, i) => {
        if (i > 0) nodes.push(<br key={`br-${i}`} />);
        nodes.push(...renderInline(line));
      });
      elements.push(
        <p key={elements.length} style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.65, margin: '0 0 10px' }}>
          {nodes}
        </p>
      );
    }
    paragraphLines = [];
  };

  for (const line of lines) {
    const heading = line.match(/^#{1,3}\s+(.+)$/);
    if (heading) {
      flushParagraph();
      elements.push(
        <p key={elements.length} style={{ fontSize: 11, fontWeight: 700, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.09em', margin: '14px 0 4px' }}>
          {heading[1]}
        </p>
      );
    } else {
      paragraphLines.push(line);
    }
  }
  flushParagraph();
  return elements;
}

interface FocusCard {
  id: string;
  urgency: 'critical' | 'today' | 'week';
  category: string;
  title: string;
  body: string;
  primaryLabel: string;
  primaryRoute: string;
  secondaryLabel?: string;
  timeFlag?: string;
  onPrimary?: () => void;
}

interface SpeechRecognitionResult { transcript: string }
interface SpeechRecognitionEvent { results: { [k: number]: { [k: number]: SpeechRecognitionResult } } }
interface SpeechRecognitionInstance {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: (e: SpeechRecognitionEvent) => void;
  onerror: () => void;
  onend: () => void;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionInstance;

const COLOR_MAP = {
  danger:  { text: 'var(--danger)',  bg: 'rgba(226,85,85,0.09)',   border: 'rgba(226,85,85,0.25)' },
  warning: { text: 'var(--gold)',    bg: 'var(--gold-dim)',         border: 'rgba(232,155,48,0.28)' },
  success: { text: 'var(--success)', bg: 'rgba(31,157,110,0.08)',  border: 'rgba(31,157,110,0.22)' },
  info:    { text: 'var(--sky)',     bg: 'rgba(110,168,255,0.08)', border: 'rgba(110,168,255,0.22)' },
  muted:   { text: 'var(--muted)',   bg: 'rgba(92,98,115,0.06)',   border: 'transparent' },
};

const URGENCY_COLOR: Record<string, 'danger' | 'warning' | 'info'> = {
  critical: 'danger',
  today:    'warning',
  week:     'info',
};

const URGENCY_LABEL: Record<string, string> = {
  critical: 'CRITICAL',
  today:    'TODAY',
  week:     'THIS WEEK',
};

function matchRelationToMeeting(meeting: any, relations: any[]): any | null {
  if (!meeting || !Array.isArray(relations) || relations.length === 0) return null;
  const title = (meeting.title || '').toLowerCase();
  const desc = (meeting.description || '').toLowerCase();
  const attendees: any[] = Array.isArray(meeting.attendees) ? meeting.attendees : [];
  const attendeeEmails = attendees
    .map((a: any) => (typeof a === 'string' ? a : a?.email || ''))
    .map((s: string) => String(s).toLowerCase())
    .filter(Boolean);
  const attendeeNames = attendees
    .map((a: any) => (typeof a === 'string' ? '' : a?.name || ''))
    .map((s: string) => String(s).toLowerCase())
    .filter(Boolean);
  const attendeeBlob = [...attendeeEmails, ...attendeeNames].join(' ');

  // Pass 1: exact email match against r.email (strongest signal when present)
  for (const r of relations) {
    const email = String(r.email || '').toLowerCase().trim();
    if (email && attendeeEmails.includes(email)) return r;
  }
  // Pass 2: name in attendees, title, or description
  for (const r of relations) {
    const name = (r.name || '').toLowerCase().trim();
    if (!name) continue;
    const first = name.split(/\s+/)[0];
    if (
      (name.length > 2 && (title.includes(name) || desc.includes(name) || attendeeBlob.includes(name))) ||
      (first.length > 2 && (attendeeNames.some(n => n.includes(first)) || title.includes(` ${first} `) || title.startsWith(`${first} `) || title.endsWith(` ${first}`)))
    ) {
      return r;
    }
  }
  return null;
}

function buildFocusCards(data: any, remindersData: any, relationsList: any[], onReminderDone: (id: string) => void, onPrepRelation: (r: any) => void): FocusCard[] {
  const cards: FocusCard[] = [];
  const now = Date.now();

  const overdueReminders = remindersData?.overdue ?? [];
  if (overdueReminders.length > 0) {
    const r = overdueReminders[0];
    cards.push({
      id: `reminder-${r.id}`,
      urgency: 'critical',
      category: 'REMINDER',
      title: r.title,
      body: overdueReminders.length > 1
        ? `This is overdue. You have ${overdueReminders.length} overdue reminders total.`
        : 'This reminder is overdue and needs your attention.',
      primaryLabel: 'Mark done',
      primaryRoute: '/tasks',
      secondaryLabel: 'View all',
      timeFlag: 'Overdue',
      onPrimary: () => onReminderDone(r.id),
    });
  }

  const fm = data?.fitness_mission;
  if (fm?.has_profile) {
    cards.push({
      id: 'fitness-mission',
      urgency: 'today',
      category: 'FITNESS',
      title: fm.headline,
      body: fm.rationale,
      primaryLabel: 'Open Fitness',
      primaryRoute: '/fitness',
      timeFlag: fm.already_logged ? 'Logged ✓'
        : fm.adjusted ? 'Adjusted by AXIS'
        : fm.readiness_label && fm.readiness_label !== 'unknown' ? `Readiness ${fm.readiness_label}` : undefined,
    });
  }

  const fin = data?.finance;
  if (fin?.available_cash != null && Number(fin.available_cash) <= 0) {
    cards.push({
      id: 'finance-zero-cash',
      urgency: 'critical',
      category: 'FINANCE',
      title: 'Cash liquidity is zero or overdrawn',
      body: 'No buffer available. Initiate a transfer or activate a credit facility before your next obligation.',
      primaryLabel: 'Open Finance',
      primaryRoute: '/finance',
      timeFlag: 'Act now',
    });
  }

  const urgentPayable = fin?.next_payables?.find((p: any) => (p.days_until ?? 99) <= 1);
  if (urgentPayable) {
    const amt = Number(urgentPayable.amount).toLocaleString('en-US', { maximumFractionDigits: 0 });
    const due = urgentPayable.days_until === 0 ? 'today' : 'tomorrow';
    cards.push({
      id: `payable-${urgentPayable.id ?? 'main'}`,
      urgency: urgentPayable.days_until === 0 ? 'critical' : 'today',
      category: 'FINANCE',
      title: `AED ${amt} — ${urgentPayable.name} due ${due}`,
      body: `This payable is ${due === 'today' ? 'due today' : 'due tomorrow'}. Ensure funds are ready and payment is processed.`,
      primaryLabel: 'View Finance',
      primaryRoute: '/finance',
      timeFlag: due === 'today' ? 'Due today' : 'Due tomorrow',
    });
  }

  // Pre-meeting briefing window: surface a Prep card per meeting in the next 60 minutes
  // (urgency flips to 'critical' inside the 30-minute window)
  const upcomingMeetings = (data?.calendar_today || []).filter((e: any) => {
    const start = new Date(e.start_time).getTime();
    return start > now - 5 * 60000 && start - now <= 60 * 60 * 1000;
  });
  const matchedIds = new Set<string>();
  for (const mtg of upcomingMeetings) {
    const matchedRel = matchRelationToMeeting(mtg, relationsList);
    if (!matchedRel) continue;
    const mins = Math.round((new Date(mtg.start_time).getTime() - now) / 60000);
    const timeStr = new Date(mtg.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    matchedIds.add(mtg.id);
    cards.push({
      id: `prep-${matchedRel.id}-${mtg.id}`,
      urgency: mins <= 30 ? 'critical' : 'today',
      category: 'PREP',
      title: `Prep for ${matchedRel.name}`,
      body: `Meeting at ${timeStr}${mtg.location ? ` · ${mtg.location}` : ''}. ${[matchedRel.company, matchedRel.role].filter(Boolean).join(' · ') || 'Pull the brief — context, open loops, and what to talk about.'}`,
      primaryLabel: 'Open briefing',
      primaryRoute: '/relations',
      timeFlag: mins > 0 ? (mins < 60 ? `in ${mins}min` : `in ${Math.round(mins / 60)}h`) : 'Starting now',
      onPrimary: () => onPrepRelation(matchedRel), // handlePrimary returns early when onPrimary is set, so default navigate is skipped
    });
  }
  // Fallback: surface a single generic calendar card if nothing matched in the window
  const fallbackMeeting = upcomingMeetings.find((m: any) => !matchedIds.has(m.id));
  if (fallbackMeeting && matchedIds.size === 0) {
    const mins = Math.round((new Date(fallbackMeeting.start_time).getTime() - now) / 60000);
    const timeStr = new Date(fallbackMeeting.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    cards.push({
      id: `meeting-${fallbackMeeting.id}`,
      urgency: mins <= 30 ? 'critical' : 'today',
      category: 'CALENDAR',
      title: fallbackMeeting.title,
      body: `Starting at ${timeStr}${fallbackMeeting.location ? ` · ${fallbackMeeting.location}` : ''}. ${fallbackMeeting.description || ''}`.trim(),
      primaryLabel: 'Open Calendar',
      primaryRoute: '/today',
      timeFlag: mins > 0 ? (mins < 60 ? `in ${mins}min` : `in ${Math.round(mins / 60)}h`) : 'Starting now',
    });
  }

  const critRisk = data?.risks?.find((r: any) => r.severity === 'critical' || r.severity === 'high');
  if (critRisk) {
    cards.push({
      id: `risk-${critRisk.id}`,
      urgency: critRisk.severity === 'critical' ? 'critical' : 'today',
      category: 'RISK',
      title: critRisk.title,
      body: critRisk.description || 'This risk requires your attention.',
      primaryLabel: 'Review in Command',
      primaryRoute: '/command',
      timeFlag: critRisk.severity === 'critical' ? 'Critical' : 'High',
    });
  }

  const coldRelations = data?.cold_relations ?? [];
  if (coldRelations.length > 0) {
    const top = coldRelations[0];
    const daysSince = top.days_since ?? '?';
    const dealStr = top.deal_value > 0 ? ` · AED ${Number(top.deal_value).toLocaleString('en-US', { maximumFractionDigits: 0 })} deal` : '';
    cards.push({
      id: `relation-${top.id}`,
      urgency: daysSince > 45 ? 'critical' : 'today',
      category: 'RELATIONS',
      title: `${top.name} — ${daysSince} days silent${dealStr}`,
      body: `${top.company ? top.company + ' · ' : ''}Last contact ${daysSince} days ago. ${coldRelations.length > 1 ? `${coldRelations.length - 1} other cold contacts.` : ''}`,
      primaryLabel: 'Open Relations',
      primaryRoute: '/relations',
      timeFlag: daysSince > 45 ? 'Relationship at risk' : 'Getting cold',
    });
  }

  const staleDecisions = data?.stale_decisions ?? [];
  if (staleDecisions.length > 0) {
    const top = staleDecisions[0];
    cards.push({
      id: `decision-${top.id}`,
      urgency: (top.days_pending ?? 0) > 14 ? 'today' : 'week',
      category: 'DECISION',
      title: top.title,
      body: `Pending ${top.days_pending} day${top.days_pending !== 1 ? 's' : ''}. ${staleDecisions.length > 1 ? `${staleDecisions.length} decisions total need your call.` : 'This needs your call.'}`,
      primaryLabel: 'Go to Command',
      primaryRoute: '/command',
      timeFlag: `${top.days_pending}d pending`,
    });
  }

  const delegItems = data?.delegation ?? [];
  if (delegItems.length > 0) {
    cards.push({
      id: 'delegation-queue',
      urgency: 'week',
      category: 'DELEGATION',
      title: `${delegItems.length} item${delegItems.length > 1 ? 's' : ''} waiting for your review`,
      body: `${delegItems.slice(0, 2).map((d: any) => d.title || d.task).join(', ')}${delegItems.length > 2 ? ` and ${delegItems.length - 2} more.` : '.'}`,
      primaryLabel: 'Review queue',
      primaryRoute: '/command',
      timeFlag: `${delegItems.length} pending`,
    });
  }

  return cards;
}

const DUBAI_TZ = 'Asia/Dubai';

function dubaiTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true, timeZone: DUBAI_TZ,
    });
  } catch {
    return '';
  }
}

function priorityColor(p: string): string {
  const v = (p || '').toLowerCase();
  if (v === 'high' || v === 'urgent' || v === 'critical') return 'var(--danger)';
  if (v === 'low') return 'var(--subtle)';
  return 'var(--gold)';
}

interface TimelineItem {
  key: string;
  kind: 'meeting' | 'task';
  title: string;
  group: 'allday' | 'timed' | 'untimed';
  sortTs: number;
  timeLabel: string;
  priority?: string;
  location?: string;
  reminderId?: string;
  origin?: string;
}

function buildTimelineItems(calendar: any[], reminders: any[]): TimelineItem[] {
  const items: TimelineItem[] = [];

  for (const e of calendar || []) {
    const allDay = !!e.is_all_day || !e.start_time;
    items.push({
      key: `mtg-${e.id}`,
      kind: 'meeting',
      title: e.title || '(untitled meeting)',
      group: allDay ? 'allday' : 'timed',
      sortTs: e.start_time ? new Date(e.start_time).getTime() : 0,
      timeLabel: allDay ? 'All day' : dubaiTime(e.start_time),
      location: e.location || undefined,
      origin: e.origin || undefined,
    });
  }

  for (const r of reminders || []) {
    if (r.status && r.status !== 'pending' && r.status !== 'snoozed') continue;
    const timed = !!r.is_time_specific && !!r.due_at;
    items.push({
      key: `task-${r.id}`,
      kind: 'task',
      title: r.title || '(untitled task)',
      group: timed ? 'timed' : 'untimed',
      sortTs: timed ? new Date(r.due_at).getTime() : Number.MAX_SAFE_INTEGER,
      timeLabel: timed ? dubaiTime(r.due_at) : 'Anytime',
      priority: r.priority || undefined,
      reminderId: r.id,
    });
  }

  // All-day events first, then timed items chronologically, then untimed tasks.
  const rank: Record<TimelineItem['group'], number> = { allday: 0, timed: 1, untimed: 2 };
  items.sort((a, b) => {
    if (rank[a.group] !== rank[b.group]) return rank[a.group] - rank[b.group];
    if (a.group === 'timed') return a.sortTs - b.sortTs;
    return 0;
  });
  return items;
}

function TodayTimeline({ calendar, reminders, onReminderDone }: {
  calendar: any[];
  reminders: any[];
  onReminderDone: (id: string) => void;
}) {
  const items = buildTimelineItems(calendar, reminders);
  const now = Date.now();
  const nowLabel = dubaiTime(new Date().toISOString());
  const firstUpcomingIdx = items.findIndex((it) => it.group === 'timed' && it.sortTs >= now);

  const rows: React.ReactNode[] = [];
  items.forEach((it, i) => {
    if (i === firstUpcomingIdx) {
      rows.push(
        <div key="now-marker" style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '3px 0' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--gold)', letterSpacing: '0.04em', minWidth: 58, textAlign: 'right' }}>
            NOW {nowLabel}
          </span>
          <span style={{ flex: 1, height: 1, background: 'var(--gold)', opacity: 0.45 }} />
        </div>
      );
    }
    const isPast = it.group === 'timed' && it.sortTs < now;
    const isTask = it.kind === 'task';
    rows.push(
      <div key={it.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 0', opacity: isPast ? 0.45 : 1 }}>
        <span className="font-mono-tabular" style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', minWidth: 58, textAlign: 'right', paddingTop: 1 }}>
          {it.timeLabel}
        </span>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: isTask ? 'var(--sky)' : 'var(--gold)', marginTop: 6, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {it.title}
            </span>
            {isTask && it.priority && (
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: priorityColor(it.priority), flexShrink: 0 }}>
                {it.priority}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'var(--subtle)', marginTop: 1, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span>{isTask ? 'Task' : `Meeting${it.location ? ` · ${it.location}` : ''}`}</span>
            {!isTask && it.origin === 'gmail_import' && (
              <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.03em', color: 'var(--sky)', border: '1px solid var(--sky)', borderRadius: 6, padding: '0 5px', lineHeight: '14px', flexShrink: 0 }}>
                auto-added from email
              </span>
            )}
            {!isTask && it.origin === 'axis' && (
              <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.03em', color: 'var(--gold)', border: '1px solid var(--gold)', borderRadius: 6, padding: '0 5px', lineHeight: '14px', flexShrink: 0 }}>
                added by AXIS
              </span>
            )}
          </div>
        </div>
        {isTask && it.reminderId && (
          <button
            onClick={() => onReminderDone(it.reminderId!)}
            aria-label="Mark done"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--muted)', display: 'flex', flexShrink: 0 }}
          >
            <CheckCircle size={18} strokeWidth={1.8} />
          </button>
        )}
      </div>
    );
  });

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>
        Today's Timeline
      </div>
      <div className="lumen-glass lumen-spring" style={{ borderRadius: 20, padding: '14px 18px' }}>
        {items.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', padding: '6px 0 4px' }}>
            Nothing scheduled today.
          </p>
        ) : rows}
      </div>
    </div>
  );
}

function OneThingCard({ card }: { card: FocusCard }) {
  const navigate = useNavigate();
  const urgencyColor = URGENCY_COLOR[card.urgency];
  const c = COLOR_MAP[urgencyColor];

  const handlePrimary = () => {
    trackEvent('today_one_thing_action', {
      category: ['PREP', 'MONEY', 'PEOPLE', 'TASK', 'VENTURE'].includes(card.category) ? card.category.toLowerCase() : 'other',
      urgency: card.urgency,
    });
    if (card.onPrimary) {
      card.onPrimary();
      return;
    }
    navigate(card.primaryRoute);
  };

  const aedMatch = card.title.match(/^(AED\s+[\d,\.]+)(.*)$/);
  const titleNode = aedMatch ? (
    <>
      <span className="font-mono-tabular" style={{ letterSpacing: '-0.01em' }}>{aedMatch[1]}</span>
      {aedMatch[2]}
    </>
  ) : card.title;

  return (
    <div className="lumen-spring lumen-card-solid" style={{
      borderRadius: 24,
      padding: '22px 22px 22px',
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: 10, fontWeight: 800, color: c.text,
            background: c.bg, padding: '4px 9px', borderRadius: 6,
            letterSpacing: '0.12em', textTransform: 'uppercase',
          }}>
            {URGENCY_LABEL[card.urgency]} · {card.category}
          </span>
        </div>
        {card.timeFlag && (
          <span style={{ fontSize: 11, color: urgencyColor === 'danger' ? c.text : 'var(--muted)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
            {urgencyColor === 'danger' && <AlertCircle size={12} />}
            {card.timeFlag}
          </span>
        )}
      </div>

      <h2 className="font-fraunces" style={{
        position: 'relative',
        fontSize: 28,
        fontWeight: 500,
        color: 'var(--text)',
        lineHeight: 1.18,
      }}>
        {titleNode}
      </h2>

      {card.body && (
        <p style={{
          position: 'relative',
          fontSize: 14,
          color: 'var(--muted)',
          lineHeight: 1.6,
          margin: 0,
        }}>
          {card.body}
        </p>
      )}

      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button
          onClick={handlePrimary}
          style={{
            padding: '12px 22px',
            background: urgencyColor === 'danger'
              ? 'linear-gradient(135deg, #EF6E6E, var(--danger))'
              : urgencyColor === 'warning'
                ? 'linear-gradient(135deg, var(--gold-light), var(--gold))'
                : 'linear-gradient(135deg, var(--sky-light), var(--sky))',
            color: '#fff',
            border: 'none',
            borderRadius: 999,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
            letterSpacing: '0.005em',
            boxShadow: urgencyColor === 'danger'
              ? '0 8px 22px rgba(226, 85, 85, 0.30)'
              : '0 8px 22px rgba(232, 155, 48, 0.30)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {card.primaryLabel}
          <span aria-hidden style={{ fontSize: 16, lineHeight: 1, transform: 'translateY(-1px)' }}>→</span>
        </button>
        {card.secondaryLabel && (
          <button
            onClick={() => navigate(card.primaryRoute)}
            style={{
              padding: '12px 18px',
              background: 'transparent',
              color: 'var(--muted)',
              border: '1px solid var(--border)',
              borderRadius: 999,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              letterSpacing: '0.005em',
            }}
          >
            {card.secondaryLabel}
          </button>
        )}
      </div>
    </div>
  );
}

function OvernightPill({ count, label, highlight }: { count: number; label: string; highlight?: boolean }) {
  const active = count > 0;
  const isAmber = active && highlight;
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      padding: '5px 10px',
      borderRadius: 999,
      background: isAmber ? 'var(--gold-dim)' : 'rgba(20,20,40,0.04)',
      fontSize: 12,
      fontWeight: 500,
      color: isAmber ? 'var(--gold)' : 'var(--muted)',
    }}>
      {label} {count}
    </span>
  );
}

function OvernightDigest({
  brief,
  generatingBrief,
  onRegenerate,
  overnightCounts,
  mission,
  onReadinessSaved,
}: {
  brief: { content: string; generated_at?: string } | null;
  generatingBrief: boolean;
  onRegenerate: () => void;
  overnightCounts: { captures: number; reminders: number; signals: number };
  mission?: any;
  onReadinessSaved?: () => void;
}) {
  const hasBrief = !!brief?.content;
  const totalCount = overnightCounts.captures + overnightCounts.reminders + overnightCounts.signals;
  const [checkinOpen, setCheckinOpen] = useState(false);
  const needsReadiness = !!(
    mission?.has_profile && !mission.already_logged && !mission.readiness_checked_in && mission.whoop_recovery == null
  );

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>
        What AXIS handled overnight
      </div>

      <div className="lumen-glass lumen-spring" style={{ borderRadius: 20, padding: '18px 18px' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: hasBrief || generatingBrief ? 16 : 8 }}>
          <OvernightPill count={overnightCounts.captures} label="Captures" />
          <OvernightPill count={overnightCounts.reminders} label="Reminders" />
          <OvernightPill count={overnightCounts.signals} label="Signals" highlight />
        </div>

        {totalCount === 0 && !hasBrief && !generatingBrief && (
          <p style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', padding: '4px 0 2px' }}>
            Quiet night. Nothing new since midnight.
          </p>
        )}

        {hasBrief && <div>{renderBriefContent(brief.content)}</div>}

        {hasBrief && needsReadiness && !checkinOpen && (
          <button
            onClick={() => setCheckinOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              marginTop: 12, padding: '12px 14px', borderRadius: 14,
              border: '1px solid rgba(232,155,48,0.28)', background: 'var(--gold-dim)',
              cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
            }}
          >
            <Activity size={15} style={{ color: 'var(--gold)', flexShrink: 0 }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', flex: 1 }}>
              Quick readiness check-in
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--gold)' }}>
              Check in →
            </span>
          </button>
        )}

        {hasBrief && needsReadiness && checkinOpen && (
          <div style={{ marginTop: 4 }}>
            <ReadinessCheckIn
              mission={mission}
              onSaved={() => { setCheckinOpen(false); onReadinessSaved?.(); }}
            />
          </div>
        )}

        {generatingBrief && !hasBrief && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 4 }}>
            <div style={{ display: 'flex', gap: 4 }}>
              <div className="lumen-orb" style={{ width: 6, height: 6 }} />
              <div className="lumen-orb" style={{ width: 6, height: 6, animationDelay: '0.15s' }} />
              <div className="lumen-orb" style={{ width: 6, height: 6, animationDelay: '0.3s' }} />
            </div>
            <p style={{ fontSize: 13, color: 'var(--muted)' }}>Generating your morning brief…</p>
          </div>
        )}

        {brief?.generated_at && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 12,
          }}>
            <span className="font-mono-tabular" style={{ fontSize: 11, color: 'var(--subtle)' }}>
              Generated {new Date(brief.generated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
            <button
              onClick={onRegenerate}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--muted)', fontSize: 12, fontFamily: 'inherit', padding: 0,
              }}
            >
              <RefreshCw size={12} /> {generatingBrief ? 'Generating…' : 'Regenerate'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function InlineCapture() {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recRef = useRef<SpeechRecognitionInstance | null>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setAttachError(null);
    const room = MAX_FILES_PER_MESSAGE - pending.length;
    if (room <= 0) { setAttachError(`Max ${MAX_FILES_PER_MESSAGE} files`); return; }
    const toUpload = Array.from(files).slice(0, room);
    for (const file of toUpload) {
      if (file.size > MAX_UPLOAD_BYTES) {
        setAttachError(`${file.name} is over 10MB`);
        continue;
      }
      const tempId = `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
      setPending((p) => [...p, { tempId, filename: file.name, size: file.size, status: 'uploading', previewUrl }]);
      try {
        const att = await uploadAttachment(file);
        if (att.ai_status === 'failed') {
          setPending((p) => p.map((x) => x.tempId === tempId
            ? { ...x, status: 'error', attachment: att, error: att.error || 'Could not analyze file' }
            : x));
        } else {
          setPending((p) => p.map((x) => x.tempId === tempId ? { ...x, status: 'done', attachment: att } : x));
        }
      } catch (e: any) {
        const msg = e?.response?.data?.error || e?.message || 'Upload failed';
        setPending((p) => p.map((x) => x.tempId === tempId ? { ...x, status: 'error', error: msg } : x));
      }
    }
  };

  const removeAttachment = (tempId: string) => {
    setPending((p) => {
      const target = p.find((x) => x.tempId === tempId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return p.filter((x) => x.tempId !== tempId);
    });
  };

  const send = () => {
    const msg = text.trim();
    const ready = pending.filter((a) => a.status === 'done' && a.attachment);
    if (!msg && ready.length === 0) return;
    if (pending.some((a) => a.status === 'uploading')) return;
    setText('');
    setPending([]);
    navigate('/chat', {
      state: {
        autoSend: msg || (ready.length === 1 ? `Attached ${ready[0].filename}` : `Attached ${ready.length} files`),
        attachment_ids: ready.map((a) => a.attachment!.id),
      },
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const handleMicTap = () => {
    if (isRecording && recRef.current) {
      try { recRef.current.stop(); } catch (err) { console.warn('[Today] speech recognizer stop failed:', err); }
      return;
    }
    const w = window as Window & {
      SpeechRecognition?: SpeechRecognitionCtor;
      webkitSpeechRecognition?: SpeechRecognitionCtor;
    };
    const SR = w.SpeechRecognition || w.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.lang = 'en-US';
    rec.continuous = false;
    rec.interimResults = false;
    rec.onresult = (e) => {
      const t = e.results[0][0].transcript;
      setText(prev => prev ? prev + ' ' + t : t);
      inputRef.current?.focus();
    };
    rec.onerror = () => { setIsRecording(false); recRef.current = null; };
    rec.onend = () => { setIsRecording(false); recRef.current = null; };
    recRef.current = rec;
    rec.start();
    setIsRecording(true);
  };

  useEffect(() => () => {
    if (recRef.current) {
      try { recRef.current.abort(); } catch (err) { console.warn('[Today] speech recognizer abort failed:', err); }
      recRef.current = null;
    }
  }, []);

  const stillUploading = pending.some((a) => a.status === 'uploading');
  const hasReady = pending.some((a) => a.status === 'done');
  const canSend = !stillUploading && (text.trim().length > 0 || hasReady);

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>
        Quick capture
      </div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPT_ATTR}
        style={{ display: 'none' }}
        onChange={(e) => {
          handleFiles(e.target.files);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }}
      />
      <AttachmentChips attachments={pending} onRemove={removeAttachment} />
      {attachError && (
        <div style={{ fontSize: 11, color: '#EF4444', padding: '0 4px 6px' }}>{attachError}</div>
      )}
      <div className="lumen-glass" style={{
        display: 'flex', alignItems: 'center', gap: 6,
        borderRadius: 999, padding: '6px 6px 6px 6px',
      }}>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={pending.length >= MAX_FILES_PER_MESSAGE}
          aria-label="Attach files"
          style={{
            width: 34, height: 34, borderRadius: '50%',
            background: 'transparent', border: 'none',
            color: pending.length >= MAX_FILES_PER_MESSAGE ? 'var(--subtle)' : 'var(--muted)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: pending.length >= MAX_FILES_PER_MESSAGE ? 'default' : 'pointer',
            flexShrink: 0,
          }}
        >
          <Paperclip size={16} />
        </button>
        <button
          onClick={handleMicTap}
          aria-label={isRecording ? 'Stop recording' : 'Start voice capture'}
          style={{
            width: 34, height: 34, borderRadius: '50%',
            background: isRecording ? 'var(--gold-dim)' : 'transparent',
            border: 'none',
            color: isRecording ? 'var(--gold)' : 'var(--muted)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', flexShrink: 0,
            transition: 'background 0.15s, color 0.15s',
          }}
        >
          <Mic size={18} />
        </button>
        <input
          ref={inputRef}
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask AXIS or capture a thought…"
          aria-label="Quick capture to AXIS"
          style={{
            flex: 1, minWidth: 0, background: 'transparent', border: 'none',
            outline: 'none', color: 'var(--text)', fontSize: 14,
            fontFamily: 'inherit', padding: '8px 0',
          }}
        />
        <button
          onClick={send}
          disabled={!canSend}
          aria-label="Send to AXIS"
          style={{
            width: 34, height: 34, borderRadius: '50%',
            background: canSend ? 'var(--gold)' : 'rgba(20,20,40,0.04)',
            color: canSend ? '#fff' : 'var(--subtle)',
            border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: canSend ? 'pointer' : 'default', flexShrink: 0,
            transition: 'background 0.2s, color 0.2s',
          }}
        >
          <Send size={14} style={{ marginLeft: 1 }} />
        </button>
      </div>
    </div>
  );
}

function ReadinessCheckIn({ mission, onSaved }: { mission: any; onSaved: () => void }) {
  const hasWhoop = mission?.whoop_recovery != null;
  const [expanded, setExpanded] = useState(!hasWhoop);
  const [form, setForm] = useState({ energy: 3, sleep_quality: 3, soreness: 3 });
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState({ motivation: 3, stress: 3 });
  const [pain, setPain] = useState(0);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = detailOpen ? { ...form, ...detail, pain, notes: note.trim() || undefined } : form;
      await api.post('/fitness/readiness', payload);
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not save check-in');
      setSaving(false);
    }
  };

  const fields: { key: keyof typeof form; label: string; invert?: boolean }[] = [
    { key: 'energy', label: 'Energy' },
    { key: 'sleep_quality', label: 'Sleep' },
    { key: 'soreness', label: 'Soreness', invert: true },
  ];

  const detailFields: { key: keyof typeof detail; label: string; invert?: boolean }[] = [
    { key: 'motivation', label: 'Motivation' },
    { key: 'stress', label: 'Stress', invert: true },
  ];

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>
        Daily readiness
      </div>
      <div className="lumen-glass lumen-spring" style={{ borderRadius: 20, padding: '16px 18px' }}>
        {hasWhoop && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: expanded ? 14 : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                fontSize: 11, fontWeight: 700, color: 'var(--success)',
                background: 'rgba(31,157,110,0.10)', padding: '4px 10px', borderRadius: 999,
              }}>
                Whoop recovery {mission.whoop_recovery}%
              </span>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>synced — your mission is adapted</span>
            </div>
            {!expanded && (
              <button
                onClick={() => setExpanded(true)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gold)', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', padding: 0, flexShrink: 0 }}
              >
                Fine-tune
              </button>
            )}
          </div>
        )}

        {expanded && (
          <>
            <p style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600, margin: '0 0 12px' }}>
              {hasWhoop ? 'Add how you feel (optional)' : 'How are you feeling today?'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {fields.map((f) => (
                <div key={f.key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                    <span style={{ color: 'var(--muted)' }}>{f.label}{f.invert ? ' (lower is better)' : ''}</span>
                    <span style={{ color: 'var(--gold)', fontWeight: 600 }}>{form[f.key]}/5</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[1, 2, 3, 4, 5].map((n) => {
                      const active = form[f.key] === n;
                      return (
                        <button
                          key={n}
                          onClick={() => setForm((s) => ({ ...s, [f.key]: n }))}
                          style={{
                            flex: 1, height: 34, borderRadius: 10, fontSize: 13, fontWeight: 600,
                            cursor: 'pointer', fontFamily: 'inherit',
                            border: active ? 'none' : '1px solid var(--border)',
                            background: active ? 'linear-gradient(135deg, var(--gold-light), var(--gold))' : 'transparent',
                            color: active ? '#fff' : 'var(--muted)',
                            transition: 'background 0.15s, color 0.15s',
                          }}
                        >
                          {n}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {!detailOpen ? (
              <button
                onClick={() => setDetailOpen(true)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gold)', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', padding: 0, marginTop: 12 }}
              >
                + More detail
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
                {detailFields.map((f) => (
                  <div key={f.key}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                      <span style={{ color: 'var(--muted)' }}>{f.label}{f.invert ? ' (lower is better)' : ''}</span>
                      <span style={{ color: 'var(--gold)', fontWeight: 600 }}>{detail[f.key]}/5</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {[1, 2, 3, 4, 5].map((n) => {
                        const active = detail[f.key] === n;
                        return (
                          <button
                            key={n}
                            onClick={() => setDetail((s) => ({ ...s, [f.key]: n }))}
                            style={{
                              flex: 1, height: 34, borderRadius: 10, fontSize: 13, fontWeight: 600,
                              cursor: 'pointer', fontFamily: 'inherit',
                              border: active ? 'none' : '1px solid var(--border)',
                              background: active ? 'linear-gradient(135deg, var(--gold-light), var(--gold))' : 'transparent',
                              color: active ? '#fff' : 'var(--muted)',
                              transition: 'background 0.15s, color 0.15s',
                            }}
                          >
                            {n}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                    <span style={{ color: 'var(--muted)' }}>Pain / niggles (0 = none)</span>
                    <span style={{ fontWeight: 600, color: pain >= 7 ? 'var(--danger)' : pain >= 4 ? 'var(--gold)' : 'var(--muted)' }}>{pain}/10</span>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
                      const active = pain === n;
                      const activeBg = n >= 7 ? 'var(--danger)' : n >= 4 ? 'var(--gold)' : 'linear-gradient(135deg, var(--gold-light), var(--gold))';
                      return (
                        <button
                          key={n}
                          onClick={() => setPain(n)}
                          style={{
                            flex: 1, height: 28, borderRadius: 8, fontSize: 10, fontWeight: 600,
                            cursor: 'pointer', fontFamily: 'inherit',
                            border: active ? 'none' : '1px solid var(--border)',
                            background: active ? activeBg : 'transparent',
                            color: active ? '#fff' : 'var(--muted)',
                            transition: 'background 0.15s, color 0.15s',
                          }}
                        >
                          {n}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>Note (optional)</div>
                  <input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. tweaked my knee, slept badly"
                    maxLength={280}
                    style={{
                      width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10,
                      border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)',
                      fontSize: 13, fontFamily: 'inherit',
                    }}
                  />
                </div>
              </div>
            )}
            {error && <p style={{ fontSize: 12, color: 'var(--danger)', margin: '10px 0 0' }}>{error}</p>}
            <button
              onClick={submit}
              disabled={saving}
              style={{
                width: '100%', marginTop: 14, padding: '12px 0', borderRadius: 14,
                background: 'linear-gradient(135deg, var(--gold-light), var(--gold))',
                color: '#fff', border: 'none', fontSize: 14, fontWeight: 600,
                cursor: saving ? 'default' : 'pointer', fontFamily: 'inherit', opacity: saving ? 0.5 : 1,
                boxShadow: '0 8px 22px rgba(232, 155, 48, 0.25)',
              }}
            >
              {saving ? 'Saving…' : 'Save check-in'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function AllClearState({ navigate }: { navigate: (p: string) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, paddingTop: 40, paddingBottom: 24, textAlign: 'center' }}>
      <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(31,157,110,0.08)', border: '2px solid var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CheckCircle size={24} style={{ color: 'var(--success)' }} />
      </div>
      <div>
        <p className="font-fraunces" style={{ fontSize: 20, fontWeight: 400, color: 'var(--text)', marginBottom: 6 }}>You're clear.</p>
        <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5, maxWidth: 240 }}>No urgent items right now. AXIS will surface new priorities as they arise.</p>
      </div>
      <button
        onClick={() => navigate('/command')}
        style={{
          background: 'linear-gradient(135deg, var(--gold-light), var(--gold))',
          color: '#fff', border: 'none', borderRadius: 999,
          padding: '10px 22px', fontSize: 13, fontWeight: 600,
          cursor: 'pointer', fontFamily: 'inherit',
          boxShadow: '0 8px 22px rgba(232, 155, 48, 0.25)',
        }}
      >
        Review Command →
      </button>
    </div>
  );
}

function formatDate() {
  return new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function Today() {
  const { ceoMode, toggleCeoMode } = useUiStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const [errorDismissed, setErrorDismissed] = useState(false);
  const [generatingBrief, setGeneratingBrief] = useState(false);
  const [reminderError, setReminderError] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery(
    ['today'],
    () => api.get('/today').then((r) => r.data),
    { refetchInterval: 5 * 60 * 1000, staleTime: 5 * 60 * 1000, cacheTime: 10 * 60 * 1000, refetchOnWindowFocus: false, retry: 0 }
  );

  useEffect(() => {
    if (isError) {
      setErrorDismissed(false);
      const t = setTimeout(() => setErrorDismissed(true), 5000);
      return () => clearTimeout(t);
    }
  }, [isError]);

  const { data: remindersData, refetch: refetchReminders } = useQuery(
    ['reminders-today'],
    async () => {
      const [overdueRes, todayRes, allRes] = await Promise.allSettled([
        api.get('/reminders?overdue=true'),
        api.get('/reminders?due_today=true'),
        api.get('/reminders'),
      ]);
      return {
        overdue: overdueRes.status === 'fulfilled' ? (overdueRes.value.data.reminders || []) : [],
        today: todayRes.status === 'fulfilled' ? (todayRes.value.data.reminders || []) : [],
        all: allRes.status === 'fulfilled' ? (allRes.value.data.reminders || []) : [],
      };
    },
    { staleTime: 2 * 60 * 1000, retry: 0 }
  );

  const { data: voiceData } = useQuery(
    ['voice-recent'],
    () => api.get('/voice/recent').then(r => r.data),
    { staleTime: 2 * 60 * 1000, retry: 0 }
  );

  const { data: relationsList = [] } = useQuery<any[]>(
    ['relations'],
    () => api.get('/relations').then(r => r.data),
    { staleTime: 5 * 60 * 1000, retry: 0 }
  );

  const handlePrepRelation = useCallback((rel: any) => {
    navigate('/relations', { state: { openBriefing: { id: rel.id, name: rel.name } } });
  }, [navigate]);

  useEffect(() => {
    if (!isLoading && !isError && data && !data.brief?.content && !generatingBrief) {
      setGeneratingBrief(true);
      api.post('/briefs/generate').catch((err) => console.error('[Today] brief generation request failed:', err));
      let polls = 0;
      const interval = setInterval(async () => {
        polls++;
        try {
          const res = await api.get('/briefs/today');
          if (res.data?.brief?.content) {
            clearInterval(interval);
            setGeneratingBrief(false);
            refetch();
          }
        } catch (err) { console.error('[Today] brief poll failed:', err); }
        if (polls >= 12) { clearInterval(interval); setGeneratingBrief(false); }
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isLoading, isError, data]);

  useEffect(() => {
    if (isLoading || isError || !data) return;
    const newsEmpty = !data.news || data.news.length === 0;
    const firstItem = data.news?.[0];
    const newsStale = !!firstItem?.created_at
      && Date.now() - new Date(firstItem.created_at).getTime() > 6 * 60 * 60 * 1000;
    if (newsEmpty || newsStale) {
      api.get('/news').catch((err) => console.error('[Today] news prefetch failed:', err));
      const t = setTimeout(() => refetch(), 12000);
      return () => clearTimeout(t);
    }
  }, [isLoading, isError, data]);

  const handleReminderDone = useCallback(async (id: string) => {
    try {
      await api.put('/reminders/' + id + '/done');
      // The One Thing card is built from the separate ['reminders-today'] query,
      // so it must be refetched too — otherwise the card never advances.
      await Promise.all([refetchReminders(), refetch()]);
      trackEvent('today_task_succeeded', { action: 'complete', surface: 'today' });
    } catch (err: any) {
      trackEvent('today_task_failed', { action: 'complete', surface: 'today', reason: 'request_failed' });
      setReminderError(err?.response?.data?.error || 'Could not mark that done — try again.');
      setTimeout(() => setReminderError(null), 4000);
    }
  }, [refetch, refetchReminders]);

  const rawFocusCards = isLoading ? [] : buildFocusCards(data, remindersData, relationsList, handleReminderDone, handlePrepRelation);
  // Hoist prep card to front so an imminent meeting always wins "One Thing"
  const focusCards = (() => {
    const prepIdx = rawFocusCards.findIndex(c => c.category === 'PREP');
    if (prepIdx <= 0) return rawFocusCards;
    return [rawFocusCards[prepIdx], ...rawFocusCards.filter((_, i) => i !== prepIdx)];
  })();
  const oneThing = focusCards[0] ?? null;
  const overdueCount = remindersData?.overdue?.length ?? 0;

  const overnightCounts = (() => {
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    const cutoff = midnight.getTime();

    const captures = (voiceData?.captures || []).filter((c: any) =>
      c?.created_at && new Date(c.created_at).getTime() >= cutoff
    ).length;

    const reminders = (remindersData?.all || []).filter((r: any) =>
      r?.created_at && new Date(r.created_at).getTime() >= cutoff
    ).length;

    const fin = data?.finance;
    const urgentPayables = (fin?.next_payables || []).filter((p: any) =>
      (p?.days_until ?? 99) <= 1
    ).length;
    const cashFlag = fin?.available_cash != null && Number(fin.available_cash) <= 0 ? 1 : 0;
    const overduePayables = Number(fin?.payables_overdue || 0);
    const signals = urgentPayables + cashFlag + overduePayables;

    return { captures, reminders, signals };
  })();

  const handleRegenerate = useCallback(async () => {
    try {
      setGeneratingBrief(true);
      await api.post('/briefs/generate?force=true');
      setTimeout(() => { refetch(); setGeneratingBrief(false); }, 3000);
    } catch { setGeneratingBrief(false); }
  }, [refetch]);

  const newsFetching = isLoading || (!data?.news?.length && !isError);

  return (
    <div className="min-h-screen" style={{ background: 'transparent' }}>
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        paddingBottom: 16,
        // Stronger blur on the solid band, then a soft fade past the header
        // so scrolling content dissolves smoothly with no visible seam.
        maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 70%, rgba(0,0,0,0) 100%)',
        WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 70%, rgba(0,0,0,0) 100%)',
        backdropFilter: 'blur(20px) saturate(140%)',
        WebkitBackdropFilter: 'blur(20px) saturate(140%)',
        background: 'linear-gradient(to bottom, rgba(244,241,236,0.78) 0%, rgba(244,241,236,0.55) 60%, rgba(244,241,236,0.0) 100%)',
      }}>
        <div style={{ paddingTop: 'env(safe-area-inset-top)' }} />
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 18px', height: 56, maxWidth: 480, margin: '0 auto',
        }}>
          <span className="font-fraunces" style={{ fontSize: 22, fontWeight: 300, color: 'var(--gold)', letterSpacing: '0.04em' }}>
            <span style={{ fontWeight: 200 }}>A</span>XIS
          </span>

          <div className="lumen-glass" style={{
            padding: '4px 12px', borderRadius: 999,
            fontSize: 12, fontWeight: 500, color: 'var(--muted)',
          }}>
            {formatDate()}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={toggleCeoMode}
              style={{
                padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                border: `1px solid ${ceoMode ? 'var(--gold)' : 'var(--border)'}`,
                color: ceoMode ? 'var(--gold)' : 'var(--muted)',
                background: ceoMode ? 'var(--gold-dim)' : 'transparent',
                cursor: 'pointer', fontFamily: 'inherit',
                transition: 'all 0.2s',
              }}
            >
              CEO
            </button>
            <button
              onClick={() => navigate('/tasks')}
              style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text)', display: 'flex' }}
            >
              <Bell size={20} strokeWidth={1.8} />
              {overdueCount > 0 && (
                <span style={{
                  position: 'absolute', top: 0, right: 0,
                  background: 'var(--danger)', color: '#fff', fontSize: 8, fontWeight: 800,
                  minWidth: 15, height: 15, borderRadius: 8,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0 3px', border: '2px solid rgba(244,241,236,0.9)',
                }}>
                  {overdueCount > 9 ? '9+' : overdueCount}
                </span>
              )}
            </button>
            <div
              onClick={() => navigate('/settings')}
              style={{
                width: 32, height: 32, borderRadius: '50%',
                border: '1.5px solid var(--gold)', background: 'var(--card)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--text)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {user?.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          maxWidth: 480, margin: '0 auto', padding: '0 18px',
          paddingTop: 'calc(env(safe-area-inset-top) + 68px)',
          paddingBottom: 'calc(76px + env(safe-area-inset-bottom) + 24px)',
        }}
      >
        {isError && !errorDismissed && (
          <div style={{
            background: 'rgba(226,85,85,0.06)', border: '1px solid rgba(226,85,85,0.20)',
            borderRadius: 14, padding: '10px 14px', marginBottom: 14,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ color: 'var(--danger)', fontSize: 13 }}>Trouble connecting — some data may be outdated</span>
            <button onClick={() => setErrorDismissed(true)} style={{ color: 'rgba(226,85,85,0.5)', fontSize: 18, background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1, padding: '0 0 0 8px' }}>×</button>
          </div>
        )}

        {reminderError && (
          <div style={{
            background: 'rgba(226,85,85,0.06)', border: '1px solid rgba(226,85,85,0.20)',
            borderRadius: 14, padding: '10px 14px', marginBottom: 14,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ color: 'var(--danger)', fontSize: 13 }}>{reminderError}</span>
            <button onClick={() => setReminderError(null)} style={{ color: 'rgba(226,85,85,0.5)', fontSize: 18, background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1, padding: '0 0 0 8px' }}>×</button>
          </div>
        )}

        {!isLoading && oneThing && (
          <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10, marginTop: 4 }}>
            Today's One Thing
          </p>
        )}

        {isLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
            <CardSkeleton lines={5} showHeader />
            <CardSkeleton lines={3} showHeader />
          </div>
        )}

        {!isLoading && oneThing && <OneThingCard card={oneThing} />}

        {!isLoading && data?.fitness_mission?.has_profile && !data.fitness_mission.already_logged && !data.fitness_mission.readiness_checked_in && (
          <ReadinessCheckIn mission={data.fitness_mission} onSaved={refetch} />
        )}

        {!isLoading && focusCards.length === 0 && <AllClearState navigate={navigate} />}

        {!isLoading && (
          <TodayTimeline
            calendar={data?.calendar_today || []}
            reminders={remindersData?.today || []}
            onReminderDone={handleReminderDone}
          />
        )}

        {!isLoading && <VenturesCard data={data?.ventures} onMutated={refetch} />}

        {!isLoading && (
          <OvernightDigest
            brief={data?.brief?.content ? data.brief : null}
            generatingBrief={generatingBrief}
            onRegenerate={handleRegenerate}
            overnightCounts={overnightCounts}
            mission={data?.fitness_mission}
            onReadinessSaved={refetch}
          />
        )}

        {!isLoading && <NewsCard data={data?.news} fetching={newsFetching} />}

        {!isLoading && <InlineCapture />}
      </div>

      <BottomNav />
    </div>
  );
}

export default Today;
